import { anyPass, isEmpty, isNil } from 'ramda'
import { type CurrencyCodes } from './constants/currency'

const locale = 'en-US'

const isNilOrEmpty = (value: unknown): value is null | undefined => anyPass([isNil, isEmpty])(value)

interface Num {
  value: number
  currencyCode?: string
  showCurrency?: boolean
}

export type FormatIds =
  | 'videoP25WatchedActions'
  | 'videoP50WatchedActions'
  | 'videoP75WatchedActions'
  | 'videoP100WatchedActions'
  | 'videoWatch2S'
  | 'videoWatch6S'
  | 'qualifiedVideoViews'
  | 'costPerVideoView'
  | 'costPerQualifiedVideoView'
  | 'avgVideoAvgTimeWatchedActions'
  | 'spend'
  | 'adSpend'
  | 'revenue'
  | 'revenueDetailed'
  | 'roas'
  | 'conversions'
  | 'impressions'
  | 'costPerAction'
  | 'conversion'
  | 'percentChange'
  | 'videoViews'
  | 'clicks'
  | 'viewableImpressions'
  | 'sessions'
  | 'costPerClick'
  | 'clickThroughRate'
  | 'videoCompletionRate'
  | 'conversionRate'
  | 'costOfSales'
  | 'hookRate'
  | 'holdRate'
  | 'viewThroughRate'
  | 'costPerThousandImpressions'
  | 'averageOrderValue'
  | 'conversionValue'
  | 'percentDrop'
  | 'percentIncrease'
  | 'budget'
  | 'numeric'
  | 'dailyTarget'
  | 'dailyBudget'
  | 'dailyRemainingTarget'
  | 'dailyRemainingBudget'
  | 'dailyTargetDepletion'
  | 'dailyBudgetDepletion'
  | 'adCount'
  | 'targetMoneyInScenarioCard'

const CURRENCY_FORMAT_IDS = [
  'spend',
  'adSpend',
  'revenue',
  'revenueDetailed',
  'costPerAction',
  'costPerClick',
  'costPerThousandImpressions',
  'averageOrderValue',
  'conversionValue',
  'budget',
  'dailyTarget',
  'dailyBudget',
  'dailyRemainingTarget',
  'dailyRemainingBudget'
]

export interface FormatToMoneyProps extends Num {
  compact?: boolean
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  currencyDisplay?: Intl.NumberFormatOptions['currencyDisplay']
}

const formatToMoney = ({
  value,
  currencyCode = 'EUR',
  compact = true,
  showCurrency = true,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
  currencyDisplay = 'narrowSymbol'
}: FormatToMoneyProps): string => {
  const compactNumberFormat: Intl.NumberFormatOptions = {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }

  const defaultNumberFormat: Intl.NumberFormatOptions = {
    currency: currencyCode,
    currencyDisplay,
    minimumFractionDigits,
    maximumFractionDigits,
    ...(showCurrency && { style: 'currency' })
  }

  // const formatOptions = {...defaultNumberFormat}
  const formatOptions = compact ? { ...defaultNumberFormat, ...compactNumberFormat } : defaultNumberFormat

  return Intl.NumberFormat(locale, formatOptions).format(value)
}

export const formatToNumber = (
  number: number,
  compact = true,
  maximumFractionDigits = 1,
  minimumFractionDigits?: number
): string => {
  const compactNumberFormat: Intl.NumberFormatOptions = {
    notation: 'compact',
    compactDisplay: 'short'
  }

  const defaultNumberFormat: Intl.NumberFormatOptions = {
    style: 'decimal',
    maximumFractionDigits,
    minimumFractionDigits
  }
  const formatOptions = compact ? { ...defaultNumberFormat, ...compactNumberFormat } : defaultNumberFormat
  const formatter = new Intl.NumberFormat(locale, formatOptions)
  return formatter.format(number)
}

export interface FormatToNumberProps {
  compact?: boolean
  maximumFractionDigits?: number
  minimumFractionDigits?: number
}

export const formatToNumberWithOptions = (
  number: number,
  { compact = true, maximumFractionDigits = 1, minimumFractionDigits }: FormatToNumberProps
): string => formatToNumber(number, compact, maximumFractionDigits, minimumFractionDigits)

export const formatToPercent = (number: number, maximumFractionDigits = 0, minimumFractionDigits = 0): string => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits
  })

  return formatter.format(number)
}

export interface FormatToPercentProps {
  maximumFractionDigits?: number
  minimumFractionDigits?: number
}

export const formatToPercentWithOptions = (
  number: number,
  { maximumFractionDigits = 0, minimumFractionDigits = 0 }: FormatToPercentProps
): string => formatToPercent(number, maximumFractionDigits, minimumFractionDigits)

const formatToPercentDrop = (number: number): string => {
  const negativeNumber = (num: number): number => -Math.abs(num)
  const formatter = new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 0
  })

  return formatter.format(negativeNumber(number))
}

/** Narrow currency symbol for a currency code (e.g. `EUR` -> `€`), falling back to the code itself. */
export const getCurrencySymbol = (currencyCode: string): string => {
  try {
    const part = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol'
    })
      .formatToParts(0)
      .find((formatPart) => formatPart.type === 'currency')
    return part?.value ?? currencyCode
  } catch {
    return currencyCode
  }
}

export const hasSignificantDecimal = (number: number): boolean => {
  const decimalPart = number.toString().split('.')[1]
  if (isNilOrEmpty(decimalPart)) return false
  const isGreaterThanZero = parseInt(decimalPart[0]) > 0 && parseInt(decimalPart[0]) < 9
  return isGreaterThanZero
}

export const getFormattedValue = ({
  value,
  currencyCode,
  id,
  compact,
  showCurrency = true
}: Num & { id: FormatIds; compact?: boolean }): string => {
  switch (id) {
    case 'targetMoneyInScenarioCard':
      return formatToMoney({
        value,
        currencyCode,
        compact,
        showCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        currencyDisplay: 'code'
      })
    case 'adSpend':
    case 'spend':
    case 'revenue':
    case 'conversionValue':
    case 'budget':
    case 'dailyTarget':
    case 'dailyBudget':
    case 'dailyRemainingTarget':
    case 'dailyRemainingBudget':
      return formatToMoney({
        value,
        currencyCode,
        compact,
        showCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
    case 'revenueDetailed':
      return formatToMoney({
        value,
        currencyCode,
        compact,
        showCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    case 'costPerAction':
    case 'costPerClick':
    case 'costPerThousandImpressions':
    case 'costPerVideoView':
    case 'costPerQualifiedVideoView':
    case 'averageOrderValue':
      return formatToMoney({
        value,
        currencyCode,
        compact,
        showCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    case 'roas':
      return formatToPercent(value, 0)
    case 'percentChange':
    case 'clickThroughRate':
    case 'videoCompletionRate':
    case 'conversionRate':
    case 'costOfSales':
    case 'hookRate':
    case 'holdRate':
    case 'viewThroughRate':
      return formatToPercent(value, 2)
    case 'percentIncrease':
    case 'dailyTargetDepletion':
    case 'dailyBudgetDepletion':
      return formatToPercent(value)
    case 'conversions':
      return hasSignificantDecimal(value) ? formatToNumber(value, compact, 1, 1) : formatToNumber(value, compact, 0)
    case 'conversion':
    case 'impressions':
    case 'videoViews':
    case 'videoP25WatchedActions':
    case 'videoP50WatchedActions':
    case 'videoP75WatchedActions':
    case 'videoP100WatchedActions':
    case 'videoWatch2S':
    case 'videoWatch6S':
    case 'qualifiedVideoViews':
    case 'clicks':
    case 'viewableImpressions':
    case 'sessions':
    case 'adCount':
      return formatToNumber(value, compact, 0)
    case 'percentDrop':
      return formatToPercentDrop(value)
    default:
      return formatToNumber(value, compact)
  }
}

/**
 * Prefix '+' for positive values. Negatives already include '-' from Intl;
 * zero is left unsigned. Same convention as `formatNumberDifference`.
 */
export const withPositiveSign = (formatted: string, value: number): string =>
  value > 0 && !formatted.startsWith('+') ? `+${formatted}` : formatted

export const formatNumberDifference = ({
  presentValue,
  previousValue,
  id,
  currencyCode
}: {
  presentValue: number
  previousValue: number
  id: FormatIds
  currencyCode?: CurrencyCodes
}): string => {
  const difference = presentValue - previousValue

  return withPositiveSign(
    getFormattedValue({
      value: difference,
      id,
      compact: true,
      currencyCode,
      showCurrency: CURRENCY_FORMAT_IDS.includes(id)
    }),
    difference
  )
}

export default formatToMoney
